/*
 * Copyright (C) Zoomdata, Inc. 2012-2022. All rights reserved.
 */
import { loadCssBundle, loadEmbedCss } from './css';
import { ComponentTypes, PUBLISH, SUBSCRIBE, UNSUBSCRIBE, isSuccessfulTokenResponse, } from './types';
import { createModalContainer, createStyleContainer, getLogiEmbedMangerScript, getComposerEmbedScript, } from './elements';
import { createPageData, getDashboardsByOriginId, decoratePageData, loadScriptBundle, dispatchEmbedCustomEvent, resolveHTMLElement, } from './utilities';
import { EmbeddedDashboard } from './EmbeddedDashboard';
import { EmbeddedInventory, InventoryItemTypes } from './EmbeddedInventory';
import { EmbeddedSourceEditor } from './EmbeddedSourceEditor';
import { EmbeddedVisualBuilder } from './EmbeddedVisualBuilder';
import { getToken } from './authentication';
import { embedComponentEntrypoints } from './entrypoints';
import throttle from 'lodash/throttle';
import { EmbeddedLiteDashboard } from './EmbeddedLiteDashboard';
const COMPOSER_VERSIONS = ['23.4', '24.1', '24.2', '24.3', '24.4']; // major versions only
export class EmbedManager {
    config;
    componentList;
    applications = [];
    mutationObserver;
    constructor(config) {
        this.config = config; // TODO destructure config?
        this.componentList = {};
        if (this.config.getToken) {
            this.updateToken();
        }
    }
    /**
     * Publishes the given message to the given channel. Publishing a message on a channel will cause any subscribing handlers on the channel
     * to be called and passed the given message.
     *
     * The message published can either be an arbitrary object or `null`.
     * An object published to a channel is not restricted to any particular structure but a Composer dashboard will only send or react
     * to messages in the structure defined as `PublishMessage`.
     *
     * Publishing `null` is used to clear the last published message from the publisher from the channel.
     * If the last published message's `publisherId` matches the `null` message's `publisherId`,
     * the last message will be removed from the channel and subscribers will receive a `null` message.
     * If the `publisherId` of the last publshed message on the channel does not match the `null` message's `publisherId`, nothing happens.
     */
    publish(topic, message, options) {
        dispatchEmbedCustomEvent(PUBLISH, { topic, message, options });
    }
    /**
     * Attaches a subscription handler to a channel. Each time a new message is published to the channel,
     * the handler will be called and passed the newly published message.
     *
     * Calling `subscribe` returns an unsubscribe method which can be called to ensure that the handler will no longer be notified upon new messages on the channel.
     *
     * Messages passed on the channel can be an arbitrary message object or `null`.
     * Because the message objects are not restricted, the subscription handler should never assume its structure and always check
     * that the message is in the structure the handler is expecting.
     *
     * `null` messages are interpreted as a clearing of the channel and should be handled by undoing any actions taken based on the last messages
     * recieved by the handler. For example, when a Composer visual recieves a `null` message in its filtering logic,
     * it removes any filters created by previous messages on the given channel.
     *
     * If a message has been published to the channel before `subscribe` is called, the handler will be immediately executed with the latest message
     * that was published on the channel.
     */
    subscribe(topic, handler) {
        dispatchEmbedCustomEvent(SUBSCRIBE, { topic, handler });
        return () => {
            dispatchEmbedCustomEvent(UNSUBSCRIBE, { topic, handler });
        };
    }
    /**
     * Creates a Composer component in your application.
     * This method is asynchronous and returns a promise that resolves the created component.
     * The parameters input to this method include:
     * - a string that identifies the type of component you want created (the component types currently supported are `dashboard` and `visual-builder`).
     * An error occurs if an invalid component type is specified.
     * - the configuration of the component.
     * Use a standard render method (for example, `dashboard.render` or `visualBuilder.render`) to render the Composer component in your application after it has been created.
     */
    createComponent = async (type, componentParams) => {
        const bundle = embedComponentEntrypoints[type];
        if (!bundle) {
            console.warn(`"${type}" component is not supported`);
            return Promise.reject(new Error(`"${type}" component is not supported`));
        }
        const bundleName = bundle.bundleName;
        await loadScriptBundle(this.config, bundleName);
        let component;
        const { theme, notificationSettings = { enabled: true } } = componentParams;
        const isSymphonyMode = this.config.path?.endsWith('/discovery');
        // assume notifications are enabled if flag is nullish
        notificationSettings.enabled = notificationSettings.enabled ?? true;
        if (type === ComponentTypes.DASHBOARD) {
            const { dashboardId, originId, editor, application, header, suppressAutoLayoutWarning, suppressResponsiveLayout, hideRestrictedVisuals, interactivityProfileName, interactivityOverrides, initialFilters, menuEventsConfig, editorUserSettings, visualLegendType, } = componentParams;
            const dashboardsByOriginId = originId ? await getDashboardsByOriginId(this.config, originId) : {};
            const dashboardIdFromOriginId = dashboardsByOriginId?.content?.[0]?.id;
            component = new EmbeddedDashboard(theme || '', application, this.renderComponent, dashboardIdFromOriginId || dashboardId || null, originId || null, header || {}, editor, interactivityProfileName, interactivityOverrides, notificationSettings, menuEventsConfig, suppressAutoLayoutWarning, suppressResponsiveLayout, hideRestrictedVisuals, editorUserSettings, visualLegendType, initialFilters);
            component.isSymphonyMode = isSymphonyMode;
            this.componentList[component?.componentInstanceId] = component;
            return component;
        }
        if (type === ComponentTypes.LITE_DASHBOARD) {
            const { dashboardId, editor, application, header, suppressAutoLayoutWarning, suppressResponsiveLayout, hideRestrictedVisuals, interactivityProfileName, interactivityOverrides, initialFilters, menuEventsConfig, editorUserSettings, visualLegendType, availableVisTypes, } = componentParams;
            component = new EmbeddedLiteDashboard(theme || '', application, this.renderComponent, dashboardId || null, header || {}, editor, interactivityProfileName, interactivityOverrides, notificationSettings, menuEventsConfig, suppressAutoLayoutWarning, suppressResponsiveLayout, hideRestrictedVisuals, editorUserSettings, visualLegendType, initialFilters, availableVisTypes);
            component.isSymphonyMode = isSymphonyMode;
            this.componentList[component?.componentInstanceId] = component;
            return component;
        }
        if (type === ComponentTypes.VISUAL_BUILDER) {
            const { visualId, application, header, breadcrumbs, source, interactivityOverrides, menuEventsConfig, editor, visualLegendType, } = componentParams;
            component = new EmbeddedVisualBuilder(theme || '', application, this.renderComponent, visualId || null, source || {}, header || {}, breadcrumbs || {}, interactivityOverrides, notificationSettings, menuEventsConfig, editor, visualLegendType);
            component.isSymphonyMode = isSymphonyMode;
            this.componentList[component?.componentInstanceId] = component;
            return component;
        }
        if (type === ComponentTypes.INVENTORY) {
            const { inventoryItem, components, interactivityOverrides } = componentParams;
            const version = +this.config.pageData.version.substring(0, 4);
            if (inventoryItem === InventoryItemTypes.SOURCE && version < 23.2) {
                return Promise.reject(new Error(`"${type}" component is not supported`));
            }
            component = new EmbeddedInventory(theme || '', this.renderComponent, inventoryItem, components, interactivityOverrides, notificationSettings);
            component.isSymphonyMode = isSymphonyMode;
            this.componentList[component?.componentInstanceId] = component;
            return component;
        }
        if (type === ComponentTypes.SOURCE_EDITOR) {
            const { sourceId, activeTab, tabs, header, breadcrumbs, interactivityOverrides } = componentParams;
            component = new EmbeddedSourceEditor(theme || '', this.renderComponent, sourceId, activeTab, tabs, header, breadcrumbs, interactivityOverrides, notificationSettings);
            component.isSymphonyMode = isSymphonyMode;
            this.componentList[component?.componentInstanceId] = component;
            return component;
        }
        return Promise.reject(new Error(`"${type}" component is not supported`));
    };
    renderComponent = (component, element) => {
        let container;
        if (typeof element === 'string') {
            container = document.querySelector(element);
        }
        else {
            container = element;
        }
        if (container) {
            container.appendChild(component.htmlElement);
            this.instantiate(component);
            this.observeEmbedRemoval();
        }
        else {
            console.warn('there is no specified HTML element');
        }
        return { component };
    };
    instantiate(component) {
        try {
            decoratePageData(this.config); // General Embed Settings
            const { constructorName, singleInitialization } = embedComponentEntrypoints[component.componentType];
            const Application = window[constructorName];
            if (Application) {
                Application.pageData = this.config.pageData;
                const root = component.container;
                const modalId = this.config.modalContainer.id;
                const modalSelector = modalId ? `#${modalId}` : `#logi-modal-root`;
                const scopedCssSelector = `${modalSelector},#logi-embed-theme-${component.theme || 'default'},.logi-embed`;
                const options = {
                    ...this.config.pageData,
                    root,
                    scopedCssSelector,
                    ...component.getApplicationParams(),
                    modalRoot: this.config.modalContainer,
                };
                const existingSingleInitializationApp = this.applications.find((app) => app.singleInitialization);
                if (singleInitialization && existingSingleInitializationApp) {
                    // @ts-ignore this needs to be fixed
                    existingSingleInitializationApp.initComponent(options);
                }
                else {
                    const instance = new Application(options);
                    const instanceId = Date.now().toString();
                    instance.id = instanceId;
                    instance.singleInitialization = singleInitialization;
                    this.applications.push(instance);
                    component.htmlElement.setAttribute('data-logi-index', '' + instanceId);
                }
                Application.publish = this.publish;
                Application.subscribe = this.subscribe;
                const event = new Event('composer-app-initialized');
                window.dispatchEvent(event);
            }
        }
        catch (error) {
            component.htmlElement.className += ' error';
            component.htmlElement.getElementsByClassName('embedding-error-messages')[0].innerText =
                error.message;
        }
    }
    checkComponentsElements() {
        Object.keys(this.componentList).forEach((componentKey) => {
            const component = this.componentList[componentKey];
            const isComponentExist = document.querySelector(`#${component.htmlElement.id}`);
            if (!isComponentExist) {
                this.removeComponent(component.componentInstanceId);
            }
        });
    }
    observeEmbedRemoval() {
        if (!this.mutationObserver) {
            this.mutationObserver = new MutationObserver(throttle(this.checkComponentsElements.bind(this), 1000));
            this.mutationObserver.observe(document.body, { childList: true, subtree: true });
        }
    }
    destroyComponent(component) {
        if (component.componentType === ComponentTypes.INVENTORY ||
            component.componentType === ComponentTypes.SOURCE_EDITOR) {
            const currentApp = this.applications.find((app) => app.componentInstanceId === component.componentInstanceId);
            currentApp?.destroy();
        }
        else {
            const currentApp = this.applications.find((app) => app.singleInitialization);
            //@ts-ignore this needs to be fixed
            currentApp?.destroyComponent(component.componentInstanceId);
        }
        component.destroy();
        component.observerRemoval?.disconnect();
        // @ts-ignore
        component.observerRemoval = undefined;
    }
    destroyApplications() {
        //TODO: think about removing side effect there
        this.applications.forEach((app) => {
            app.destroy();
            //@ts-ignore
            app = undefined;
        });
        this.applications = [];
    }
    /**
     * Removes an embedded Composer component from your application.
     * This method returns the value `true` when the removal is successful and `false` when it fails.
     * When a component is removed, its watchers are also removed.
     * The parameter input to this method is a string representing the component instance ID.
     */
    removeComponent(id) {
        try {
            const component = this.getComponentById(id);
            const element = component.htmlElement;
            this.destroyComponent(component);
            element.parentNode?.removeChild(element);
            delete this.componentList[id];
            if (Object.keys(this.componentList).length === 0) {
                this.destroyApplications();
                this.mutationObserver.disconnect();
                // @ts-ignore;
                this.mutationObserver = null;
            }
            return true;
        }
        catch (error) {
            console.error(error);
            return false;
        }
    }
    /**
     * Refreshes a particular component. The parameter used with this method is a string representing the component instance ID.
     */
    refreshComponent(id) {
        try {
            const component = this.getComponentById(id);
            const element = component.htmlElement;
            const parent = element.parentElement;
            if (parent) {
                this.destroyComponent(component);
                parent.removeChild(element);
                component.render(parent);
                return true;
            }
            return false;
        }
        catch (error) {
            console.error(error);
            return false;
        }
    }
    /**
     * Refreshes all embedded components. This is useful when the token is expired. No parameters are passed to this method.
     */
    refresh() {
        resolveHTMLElement(this.config.modalContainer).innerHTML = '';
        this.destroyApplications();
        Object.keys(this.componentList).forEach((id) => {
            this.refreshComponent(id);
        });
    }
    /**
     * Combines an update token and refresh request for all components.
     * The parameter input to this method is either a string representing the new token or a function requesting a new token.
     */
    async refreshWithToken(tokenParam) {
        const token = await this.updateToken(tokenParam);
        this.refresh();
        return token;
    }
    /**
     * Returns an instance of a Composer component, based on its component instance ID.
     * The parameter input to this method is a string representing the component instance ID.
     */
    getComponentById(id) {
        const component = this.componentList[id];
        if (!component) {
            throw new Error(`Component with id [${id}] is not found`);
        }
        return component;
    }
    /**
     * Refreshes the authorization token for an embedded Composer component.
     * This method is asynchronous and should be called prior to a refresh.
     * The parameter input to this method is either a string representing the new token or a function requesting a new token.
     * In case parameter is not defined, this method will call the `getToken` function passed during initialization.
     */
    async updateToken(tokenParam) {
        const updateTokenWhenBootstrapped = async (_err, token) => {
            this.applications.forEach(async (app) => {
                app.updateToken(token);
            });
            //@ts-ignore this needs to be fixed
            this.config.params.token = token;
            return token;
        };
        if (!tokenParam && !this.config.getToken) {
            throw new Error('token parameter must be defined');
        }
        if (typeof tokenParam === 'string') {
            return updateTokenWhenBootstrapped(null, tokenParam);
        }
        if (typeof tokenParam === 'function') {
            return getToken(updateTokenWhenBootstrapped, tokenParam);
        }
        else if (this.config.getToken) {
            return getToken(updateTokenWhenBootstrapped, this.config.getToken);
        }
        else {
            return false;
        }
    }
}
let embedManagerPromise;
export async function initEmbedManagerModule(authorizationSettings, url, embedParameters) {
    if (await embedManagerPromise) {
        return embedManagerPromise;
    }
    // TODO extract duplication
    let token;
    let config;
    try {
        if ('getToken' in authorizationSettings) {
            const tokenResponse = await authorizationSettings.getToken();
            if (isSuccessfulTokenResponse(tokenResponse)) {
                token = tokenResponse.access_token;
            }
            else {
                return Promise.reject(tokenResponse);
            }
        }
        else {
            token = authorizationSettings.initialToken;
        }
        const serverURL = new URL(url);
        const secure = serverURL.protocol === 'https:';
        config = {
            url,
            host: serverURL.hostname,
            path: serverURL.pathname,
            secure,
            port: serverURL.port !== undefined ? parseInt(serverURL.port, 10) : secure ? 443 : 80,
            params: { token },
            serverPath: `${serverURL.origin}${serverURL.pathname}`,
            pageData: {
                staticResourceVersion: '',
                version: '',
            },
            getToken: 'getToken' in authorizationSettings ? authorizationSettings.getToken : undefined,
            modalContainer: embedParameters?.modalContainer,
            nonce: embedParameters?.nonce,
        };
        // TODO figure out if we can avoid using window here
        window.composerServerPath = config.serverPath + '/';
        loadEmbedCss(config);
        config.pageData = await createPageData(config);
        if (!checkIfVersionValid(config.pageData.version || '')) {
            const error = new Error('Unsupported Composer Version:' +
                config.pageData.version +
                '. Supported versions:' +
                COMPOSER_VERSIONS.join(','));
            return Promise.reject(error);
        }
        loadCssBundle(config);
        createModalContainer(config);
        createStyleContainer(config);
    }
    catch (error) {
        return Promise.reject(error);
    }
    embedManagerPromise = Promise.resolve(new EmbedManager(config));
    return embedManagerPromise;
}
function checkIfVersionValid(currentVersion) {
    const currentMajorVersion = currentVersion.substring(0, 4);
    return !!COMPOSER_VERSIONS.find((version) => {
        return version === currentMajorVersion;
    });
}
export async function initEmbedManager(authorizationSettings, url, embedParameters) {
    if (await window.initEmbedPromise) {
        return window.initEmbedPromise;
    }
    if (!url) {
        const scriptElement = getLogiEmbedMangerScript() || getComposerEmbedScript();
        const zoomdataUrlPattern = /(?<base>.*?)\/(?:js|misc|embed)\/embed\.js(?:\/|\?|)(?<params>.*)/i;
        const scriptComponents = zoomdataUrlPattern.exec(scriptElement.src);
        //@ts-ignore this is not part of the npm
        url = scriptComponents.groups.base;
    }
    if (!authorizationSettings || !(authorizationSettings.getToken || authorizationSettings.initialToken)) {
        return Promise.reject('authorizationSettings must be defined');
    }
    let token = '';
    let config;
    try {
        if (authorizationSettings.getToken) {
            const tokenResponse = await authorizationSettings.getToken();
            if (isSuccessfulTokenResponse(tokenResponse)) {
                token = tokenResponse.access_token;
            }
            else {
                return Promise.reject(tokenResponse);
            }
        }
        else {
            token = authorizationSettings.initialToken || '';
        }
        const serverURL = new URL(url);
        const secure = serverURL.protocol === 'https:';
        config = {
            url,
            host: serverURL.hostname,
            path: serverURL.pathname,
            secure,
            port: serverURL.port !== undefined ? parseInt(serverURL.port, 10) : secure ? 443 : 80,
            params: { token },
            serverPath: `${serverURL.origin}${serverURL.pathname}`,
            pageData: {
                staticResourceVersion: '',
                version: '',
            },
            getToken: authorizationSettings.getToken,
            modalContainer: embedParameters?.modalContainer,
            nonce: embedParameters?.nonce,
        };
        window.composerServerPath = config.serverPath + '/';
        loadEmbedCss(config);
        config.pageData = await createPageData(config);
        loadCssBundle(config);
        createModalContainer(config);
        createStyleContainer(config);
    }
    catch (error) {
        return Promise.reject(error);
    }
    window.initEmbedPromise = Promise.resolve(new EmbedManager(config));
    return window.initEmbedPromise;
}
//# sourceMappingURL=EmbedManager.js.map